// Stock count schedule texts (CR_20260914): the schedule, the status chip and the result, from count_status() rows.
import {
  type CountStatus,
  countResult,
  isoWeekday,
  type LastCount,
  STATE_TONE,
} from '@fleetspoke/core/counts';

type T = (key: string, opts?: Record<string, unknown>) => string;

/** "Daily by 18:00 · skips Sunday", "Weekly on Wednesday by 17:00", "Every 3 days by 18:00", "Not scheduled". */
export function scheduleText(
  s: Pick<CountStatus, 'every_days' | 'start_date' | 'due_time' | 'skip_days'>,
  t: T,
): string {
  if (!s.every_days || !s.start_date) return t('counts.schedule_none');
  const time = s.due_time;
  const base =
    s.every_days === 1
      ? t('counts.schedule_daily', { time })
      : s.every_days === 7
        ? t('counts.schedule_weekly', {
            day: t(`common.weekday_${isoWeekday(s.start_date)}`),
            time,
          })
        : t('counts.schedule_every', { n: s.every_days, time });
  if (!s.skip_days.length || s.every_days % 7 === 0) return base;
  const days = [...s.skip_days]
    .sort((a, b) => a - b)
    .map((d) => t(`common.weekday_${d}`))
    .join(', ');
  return `${base} · ${t('counts.schedule_skips', { days })}`;
}

/**
 * How much of an open count is left: lines still in the not_found / unverified buckets — assets nobody has ticked
 * yet, which is *not* the same as the presence engine's `missing`. "7 to find" beside "1 missing" read as a
 * contradiction (owner, 2026-09-18); one phrase, "still to check", is used wherever this number appears.
 */
export function openCountText(
  open: Pick<CountStatus['open_count'] & object, 'not_found' | 'unverified'> | null | undefined,
  t: T,
): string {
  const n = (open?.not_found ?? 0) + (open?.unverified ?? 0);
  return n ? t('counts.state_in_progress', { n }) : t('counts.state_in_progress_zero');
}

/**
 * The status chip: text and colour. `date` formats a calendar date.
 *
 * `standalone` is for the chip away from the Counts page — on a project or place card it sits beside asset figures,
 * where a bare "In progress · 3 left" said neither what was in progress nor what the 3 were, and read as a third
 * bucket of the assets next to it. There it becomes "Stock count: 3 still to check".
 */
export function stateChip(
  s: Pick<CountStatus, 'state' | 'due_time' | 'next_due' | 'open_count'>,
  t: T,
  date: (d: string) => string | null,
  opts: { standalone?: boolean } = {},
): { label: string; color: (typeof STATE_TONE)[keyof typeof STATE_TONE] } {
  const color = STATE_TONE[s.state];
  const wrap = (label: string) =>
    opts.standalone ? t('counts.chip_standalone', { text: label }) : label;
  switch (s.state) {
    case 'due':
      return { label: wrap(t('counts.state_due', { time: s.due_time })), color };
    case 'in_progress':
      return { label: wrap(openCountText(s.open_count, t)), color };
    case 'upcoming':
      return {
        label: wrap(t('counts.state_upcoming', { date: s.next_due ? date(s.next_due) : '—' })),
        color,
      };
    default:
      return { label: wrap(t(`counts.state_${s.state}`)), color };
  }
}

/**
 * How a count line was verified, for the "Verified by" column (owner, 2026-09-18: "it should not be just 'seen'").
 *
 * Two independent records: `found_method` is what the count itself found in the window (a QR scan, a gateway BLE
 * hear, the presence engine), `cleared_method` is a person's own tick. A human tick wins because it is the later,
 * deliberate act, and it is marked `by hand` so nobody mistakes somebody's word for a scan.
 */
export function verifiedBy(
  l: Pick<CountLineEvidence, 'bucket' | 'found_method' | 'cleared_method'>,
  t: T,
): { label: string; evidence: boolean } {
  if (l.cleared_method)
    return {
      label: `${t(`counts.method_${l.cleared_method}`)} (${t('counts.by_hand')})`,
      // a scan or a BLE hear made through the app is still evidence; "checked by eye" is not
      evidence: l.cleared_method !== 'visual',
    };
  if (l.found_method)
    return { label: t(`counts.method_${l.found_method}`), evidence: l.found_method !== 'presence' };
  // Nothing recorded. What that means depends on the bucket, and saying "Not verified" against a Found row read as
  // a contradiction (owner, 2026-09-18). A found line with no method is one the count matched before it started
  // recording evidence (any line written before 20260918130000); an unfound line simply has nothing to show.
  if (l.bucket === 'found' || l.bucket === 'unexpected')
    return { label: t('counts.method_unrecorded'), evidence: false };
  return { label: '—', evidence: false };
}

/** The fields `verifiedBy` needs — a subset of the count line row, so mobile can pass its own shape. */
export interface CountLineEvidence {
  bucket: string;
  /** absent, not just null, when the database predates the count-evidence migration */
  found_method?: string | null;
  cleared_method?: string | null;
}

/** "All accounted for" / "3 not found · 1 unexpected" (decision C11). */
export function resultText(
  c: Pick<LastCount, 'not_found' | 'unverified' | 'unexpected'>,
  t: T,
): {
  label: string;
  ok: boolean;
} {
  const r = countResult(c);
  const parts = [r.ok ? t('counts.result_ok') : t('counts.result_missing', { n: r.missing })];
  if (r.unexpected) parts.push(t('counts.result_unexpected', { n: r.unexpected }));
  return { label: parts.join(' · '), ok: r.ok };
}

/** Owners by name; an owner who lost access to the site is marked. Empty = anyone with access counts. */
export function ownersText(s: Pick<CountStatus, 'owners'>, t: T): string {
  if (!s.owners.length) return t('counts.owners_none');
  return s.owners
    .map((o) =>
      o.has_access ? (o.name ?? '—') : `${o.name ?? '—'} (${t('counts.owner_no_access')})`,
    )
    .join(', ');
}
